import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  CreateItemListInput,
  CreateItemListResponse,
  DeleteItemListResponse,
  ListItemListsResponse,
  UpdateItemListInput,
  UpdateItemListResponse,
} from '@erp/rpc';

/** The org's shared item lists (name, description, itemCount, creator). */
export function useItemLists(enabled = true) {
  const query = useQuery({
    ...orpc.inventoryManagement.itemLists.list.queryOptions({ input: {} }),
    enabled,
  });

  return {
    ...query,
    lists: query.data?.lists ?? [],
  } satisfies typeof query & {
    data?: ListItemListsResponse;
    lists: ListItemListsResponse['lists'];
  };
}

/**
 * One item list's metadata. `list` is null once the list has been deleted —
 * which is how the catalog's list filter notices a restored id is gone.
 */
export function useItemList(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.inventoryManagement.itemLists.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return { ...query, list: query.data?.list ?? null };
}

/** Invalidate the lists listing so a create/update/delete shows everywhere. */
function useInvalidateItemLists() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.itemLists.list.queryKey({
        input: {},
      }),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.itemLists.get.key(),
    });
  };
}

/**
 * Create an org-shared item list from product keys, pasted item numbers, or
 * the current catalog filters. Callers own the success toast — the result
 * carries `itemCount` and `unmatchedInputs` worth reporting.
 */
export function useCreateItemList() {
  const invalidate = useInvalidateItemLists();
  return useMutation<CreateItemListResponse, Error, CreateItemListInput>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.itemLists.create(input),
    onSuccess: () => invalidate(),
  });
}

/** Update an item list's name or description. */
export function useUpdateItemList() {
  const invalidate = useInvalidateItemLists();
  return useMutation<UpdateItemListResponse, Error, UpdateItemListInput>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.itemLists.update(input),
    onSuccess: () => invalidate(),
  });
}

/** Delete an item list (org-shared — it disappears for everyone). */
export function useDeleteItemList() {
  const invalidate = useInvalidateItemLists();
  return useMutation<DeleteItemListResponse, Error, { id: string }>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.itemLists.delete(input),
    onSuccess: () => invalidate(),
  });
}
